import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import CategoryTabs from '../../components/feature/CategoryTabs';
import NewsCard from '../../components/feature/NewsCard';
import ApiNewsCard from '../../components/feature/ApiNewsCard';
import LoadingSpinner from '../../components/feature/LoadingSpinner';
import ErrorMessage from '../../components/feature/ErrorMessage';
import PaginationControls from '../../components/feature/PaginationControls';
import AuthModal from '../../components/feature/AuthModal';
import { newsData } from '../../mocks/news';
import { useNewsApi } from '../../hooks/useNewsApi';
import { blogsService } from '../../services/blogs';
import type { BlogPost } from '../../services/blogs';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { siteConfigService } from '../../services/siteConfig';
import type { SiteConfig } from '../../services/siteConfig';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('Todas');
  const [activeLang, setActiveLang] = useState('todos');
  const [authModal, setAuthModal] = useState<{ isOpen: boolean; mode: 'login' | 'register' }>({
    isOpen: false,
    mode: 'login'
  });
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const { isAuthenticated } = useAuth();
  
  const categories = ['Todas', 'Literatura', 'Teatro', 'Poesía', 'Ensayo', 'Blog'];
  const langOptions = ['todos', 'es', 'en'];
  
  // Hook para datos de la API
  const { 
    articles, 
    loading, 
    error, 
    hasMore, 
    loadMore, 
    refresh,
    totalResults 
  } = useNewsApi(activeCategory === 'Poesía' ? 'Poesia' : activeCategory, activeLang);
  
  // Cargar configuración del sitio
  useEffect(() => {
    const loadSiteConfig = async () => {
      try {
        setConfigLoading(true);
        const config = await siteConfigService.getSiteConfig();
        setSiteConfig(config);
      } catch (error) {
        console.error('Error loading site config:', error);
        // Si falla la carga, usar configuración por defecto
        setSiteConfig({
          show_title: true,
          site_title: 'VINTAGE LITERAL',
          logo_url: '',
          logo_width: 40,
          logo_height: 40,
          hero_image_url: '',
          hero_image_scale: 'cover',
          hero_image_position: 'left-top',
          hero_title: 'VINTAGE LITERAL',
          hero_subtitle: 'Explora la literatura\ncon mirada retro',
        });
      } finally {
        setConfigLoading(false);
      }
    };
    
    loadSiteConfig();
  }, []);

  // Almacenar artículos en sessionStorage para acceso en la página de detalle
  useEffect(() => {
    if (articles.length > 0) {
      sessionStorage.setItem('newsApiArticles', JSON.stringify(articles));
    }
  }, [articles]);

  

  // Determinar si mostrar contenido local (solo para Blog) o API
  const showLocalContent = activeCategory === 'Blog';

  const [userBlogPosts, setUserBlogPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    // Cargar blog posts de usuarios junto con noticias
    const loadUserBlogs = async () => {
      try {
        const blogPosts = await blogsService.getPublishedBlogPosts();
        setUserBlogPosts(blogPosts.slice(0, 5)); // Mostrar solo los 5 más recientes
      } catch (error) {
        console.error('Error loading user blogs:', error);
      }
    };

    loadUserBlogs();
  }, []);

  const handleLoginClick = () => {
    if (!isAuthenticated) {
      setAuthModal({ isOpen: true, mode: 'login' });
    }
  };

  const navigate = useNavigate();

  // Determinar la imagen del hero - solo si la configuración está cargada
  const heroImageUrl = siteConfig?.hero_image_url || "https://readdy.ai/api/search-image?query=vintage%20library%20scene%20with%20antique%20typewriter%2C%20old%20wooden%20desk%20with%20brass%20details%2C%20leather-bound%20books%20stacked%20around%2C%20warm%20sepia%20lighting%2C%20nostalgic%20atmosphere%2C%20classic%20literature%20setting%2C%20aged%20paper%20manuscripts%2C%20fountain%20pen%20nearby%2C%20mahogany%20bookshelf%20background%2C%20sepia%20and%20brown%20tones%2C%20literary%20workspace%20aesthetic%2C%20dusty%20sunbeams%20through%20window%2C%20scholarly%20ambiance&width=375&height=256&seq=hero_vintage_005&orientation=landscape";

  // Mostrar spinner mientras carga la configuración
  if (configLoading || !siteConfig) {
    return (
      <div className="min-h-screen bg-lp-bg">
        <Navbar />
        <div className="pt-20">
          <LoadingSpinner message="Cargando configuración..." />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-lp-bg">
        <Navbar />
        
        {/* Hero Section - Completamente restructurado */}
        <div>
          <div className="relative w-full h-64 md:h-[50vh] lg:h-[55vh] overflow-hidden">
            {/* Imagen de fondo */}
            <div 
              className="absolute inset-0 w-full h-full"
              style={{
                backgroundImage: `url(${heroImageUrl})`,
                backgroundSize: siteConfig.hero_image_scale,
                backgroundPosition: siteConfig.hero_image_position.replace('-', ' '),
                backgroundRepeat: 'no-repeat'
              }}
            />
            
            {/* Overlays para el efecto vintage */}
            <div className="absolute inset-0 bg-gradient-to-b from-sepia-900/70 via-sepia-800/60 to-sepia-900/80" />
            <div className="absolute inset-0 bg-sepia-900/40" />
            <div className="absolute inset-0 bg-sepia-800/30 mix-blend-multiply" />
            
            {/* Contenido del hero centrado */}
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
              <h2 className="text-3xl font-headings font-bold text-sepia-100 mb-4 text-center tracking-wide" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.9), 1px 1px 4px rgba(0,0,0,0.7)' }}>
                {siteConfig.hero_title}
              </h2>
              <p className="text-sepia-200 text-base font-body text-center mb-6 opacity-95 leading-relaxed whitespace-pre-line max-w-xs" style={{ textShadow: '1px 1px 4px rgba(0,0,0,0.8)' }}>
                {siteConfig.hero_subtitle}
              </p>
              <button 
                onClick={handleLoginClick}
                className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-100/70 px-6 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md border border-sepia-300/20 shadow-sm"
              >
                Ingresar
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 pb-6 mt-6">
          {/* Header Section */}
          <div className="mb-6">
            <div className="border-b-2 border-sepia-500/50 mb-6"></div>
            
            <div className="flex flex-col items-center gap-4 mb-4">
              <h2 className="text-lg font-headings text-sepia-900 text-center">EXPLORA POR CATEGORÍAS</h2>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="mb-6">
            <CategoryTabs 
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </div>

          {/* Language Toggle */}
          {activeCategory !== 'Blog' && (
            <div className="flex justify-center gap-2 mb-6">
              {langOptions.map(lang => (
                <button
                  key={lang}
                  onClick={() => setActiveLang(lang)}
                  className={`px-4 py-2 text-sm font-ui rounded-[15%] transition-all duration-200 ${
                    activeLang === lang
                      ? 'vintage-paper-subtle text-sepia-900 shadow-md'
                      : 'text-sepia-600 hover:text-sepia-800 bg-transparent'
                  }`}
                >
                  {lang === 'todos' ? 'Todos los idiomas' : lang === 'es' ? 'Español' : 'Inglés'}
                </button>
              ))}
            </div>
          )}

          {/* User Blog Posts Section - Mostrar cuando hay blogs de usuarios */}
          {userBlogPosts.length > 0 && (
            <div className="mb-6">
              <div className="vintage-paper aged-paper p-4 shadow-lg mb-4">
                <h2 className="font-headings text-lg text-sepia-900 mb-2 flex items-center gap-2">
                  <i className="ri-edit-line text-sepia-600"></i>
                  BLOGS DE LA COMUNIDAD
                </h2>
                <p className="text-sm text-sepia-600 font-ui">
                  Contenido creado por miembros de nuestra comunidad literaria
                </p>
              </div>
              
              <div className="space-y-4">
                {userBlogPosts.map((post) => (
                  <div key={post.id} className="vintage-paper aged-paper p-4 shadow-lg">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs font-ui text-sepia-600 vintage-paper-subtle px-3 py-1 rounded-[15%]">
                        {post.category}
                      </span>
                      <span className="text-xs text-amber-700 font-ui bg-amber-100/50 px-2 py-0.5 rounded-full">
                        Blog Usuario
                      </span>
                      {post.is_affiliated && (
                        <span className="text-xs text-green-700 font-ui bg-green-100/50 px-2 py-0.5 rounded-full">
                          Afiliado
                        </span>
                      )}
                    </div>
                    
                    <h3 className="font-headings text-base text-sepia-900 mb-2 leading-snug">
                      {post.title}
                    </h3>
                    
                    <p className="text-sm text-sepia-700 font-body mb-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex justify-between items-center text-xs text-sepia-600 font-ui border-t border-sepia-300/50 pt-3">
                      <span>Por: <strong>{post.author_name}</strong></span>
                      <button
                        onClick={() => navigate(`/blog/${post.id}`)}
                        className="text-sepia-600 hover:text-sepia-800 transition-colors flex items-center gap-1"
                      >
                        <i className="ri-arrow-right-line"></i>
                        Leer más
                      </button>
                    </div>
                    
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {post.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="text-xs text-sepia-500 font-ui vintage-paper-subtle px-2 py-0.5 rounded-[10%]">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content Section */}
          <div className="space-y-4">
            {showLocalContent ? (
              <>
                {/* Local Blog Content - Render Real User Blogs */}
                {userBlogPosts.map((post) => (
                  <div key={post.id} className="vintage-paper aged-paper p-4 shadow-lg mb-4">
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs font-ui text-sepia-600 vintage-paper-subtle px-3 py-1 rounded-[15%]">
                        {post.category}
                      </span>
                      <span className="text-xs text-amber-700 font-ui bg-amber-100/50 px-2 py-0.5 rounded-full">
                        Blog Usuario
                      </span>
                      {post.is_affiliated && (
                        <span className="text-xs text-green-700 font-ui bg-green-100/50 px-2 py-0.5 rounded-full">
                          Afiliado
                        </span>
                      )}
                    </div>
                    
                    {/* Cover Image Thumbnail if available */}
                    {post.cover_image && (
                      <div className="mb-3 h-32 w-full overflow-hidden rounded-[5%] vintage-paper-subtle">
                         <img 
                           src={post.cover_image} 
                           alt={post.title} 
                           className="w-full h-full object-cover"
                         />
                      </div>
                    )}
                    
                    <h3 className="font-headings text-base text-sepia-900 mb-2 leading-snug">
                      {post.title}
                    </h3>
                    
                    <p className="text-sm text-sepia-700 font-body mb-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex justify-between items-center text-xs text-sepia-600 font-ui border-t border-sepia-300/50 pt-3">
                      <span>Por: <strong>{post.author_name}</strong></span>
                      <button
                        onClick={() => navigate(`/blog/${post.id}`)}
                        className="text-sepia-600 hover:text-sepia-800 transition-colors flex items-center gap-1"
                      >
                        <i className="ri-arrow-right-line"></i>
                        Leer más
                      </button>
                    </div>
                    
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {post.tags.slice(0, 3).map((tag, index) => (
                          <span key={index} className="text-xs text-sepia-500 font-ui vintage-paper-subtle px-2 py-0.5 rounded-[10%]">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                
                {userBlogPosts.length === 0 && (
                  <div className="vintage-paper aged-paper p-6 text-center shadow-lg">
                    <i className="ri-file-text-line text-3xl text-sepia-400 mb-3"></i>
                    <h3 className="font-headings text-lg text-sepia-900 mb-2">
                      No hay contenido de blog disponible
                    </h3>
                    <p className="text-sm text-sepia-700 font-body">
                      Pronto habrá contenido de nuestros usuarios afiliados.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* API Loading State */}
                {loading && articles.length === 0 && (
                  <LoadingSpinner message="Cargando noticias actuales..." />
                )}

                {/* API Error State */}
                {error && articles.length === 0 && (
                  <ErrorMessage message={error} onRetry={refresh} />
                )}

                {/* API Articles */}
                {articles.map((article) => (
                  <ApiNewsCard
                    key={article.article_id}
                    article={article}
                  />
                ))}

                {/* Pagination Controls */}
                <PaginationControls
                  hasMore={hasMore}
                  loading={loading}
                  onLoadMore={loadMore}
                  totalResults={totalResults}
                  currentCount={articles.length}
                />

                {/* No Results Message */}
                {!loading && !error && articles.length === 0 && (
                  <div className="vintage-paper aged-paper p-6 text-center shadow-lg">
                    <i className="ri-search-line text-3xl text-sepia-400 mb-3"></i>
                    <h3 className="font-headings text-lg text-sepia-900 mb-2">
                      No se encontraron noticias
                    </h3>
                    <p className="text-sm text-sepia-700 font-body mb-4">
                      Intenta con otra categoría o verifica tu conexión a internet.
                    </p>
                    <button 
                      onClick={refresh}
                      className="vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-2 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md"
                    >
                      Intentar de nuevo
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <div 
            className="fixed inset-0 pointer-events-none opacity-5 z-0"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20paper%20texture%2C%20old%20book%20pages%2C%20antique%20parchment%20background%2C%20sepia%20tones%2C%20weathered%20paper%20with%20subtle%20stains%20and%20aging%20marks%2C%20literary%20atmosphere%2C%20warm%20beige%20and%20cream%20colors%2C%20high%20resolution%20texture%2C%20nostalgic%20feel%2C%20classic%20book%20aesthetic&width=375&height=812&seq=vintage_bg_002&orientation=portrait')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              mixBlendMode: 'multiply'
            }}
          />
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModal.isOpen}
        onClose={() => setAuthModal({ ...authModal, isOpen: false })}
        mode={authModal.mode}
        onSwitchMode={(mode) => setAuthModal({ isOpen: true, mode })}
      />
    </>
  );
}
